import { createUseStyles, withTheme } from '@glu/theming';
import PropTypes from 'prop-types';
import locale from '@glu/locale';
import React, { useEffect, useRef } from 'react';
import moment from 'moment';
import classnames from 'classnames';
import styles from './RangeSelect.styles';

const useStyles = createUseStyles(styles);

const RangeSelect = ({
  RangeSelectProps,
  alwaysShowCalendars,
  firstFocusedElement,
  htmlId,
  onApply,
  onPresetSelection,
  rangePresets,
  selectedRangeName,
  setEditing,
  setHead,
  setSelectedEndDate,
  setSelectedRangeName,
  setSelectedStartDate,
  showCustomRangeLabel,
  ...rest
}) => {
  const classes = useStyles(rest);
  const buttonRef = useRef();
  const presets = Object.keys(rangePresets).map((key) => ({
    name: key,
    range: [moment(rangePresets[key][0]), moment(rangePresets[key][1])],
  }));

  const handlePresetClick = ({ name, range: [start, end] }) => {
    onPresetSelection(name, [start, end]);
    setSelectedRangeName(name);
    setSelectedStartDate(start);
    setSelectedEndDate(end);
    if (alwaysShowCalendars) {
      setHead(start);
      setEditing(true);
    } else {
      onApply(start, end);
    }
  };

  const handleCustomClick = () => {
    onPresetSelection(null);
    setSelectedRangeName('custom');
    setEditing(true);
  };

  /* istanbul ignore next */
  useEffect(() => {
    if (firstFocusedElement) {
      buttonRef.current.focus();
    }
  }, [firstFocusedElement]);

  const refFunction = (key) => {
    const buttonProps = {};
    if (!key) buttonProps.ref = buttonRef;
    return buttonProps;
  };

  return (
    <div className={classes.root} data-qa="range-select" role="listbox">
      {presets.map((preset, key) => (
        <button
          {...refFunction(key)}
          key={preset.name}
          aria-selected={preset.name === selectedRangeName}
          className={`${classes.option} ${
            preset.name === selectedRangeName ? classes.selected : ''
          }`}
          data-qa={`range-option-${[preset.name]}`}
          id={`${htmlId}-range-option-${[preset.name]}`}
          onClick={() => {
            handlePresetClick(preset);
          }}
          role="option"
          type="button"
        >
          {preset.name}
        </button>
      ))}
      {showCustomRangeLabel && (
        <button
          key="custom"
          aria-selected={selectedRangeName === 'custom'}
          className={classnames(classes.option, {
            [classes.selected]: selectedRangeName === 'custom',
          })}
          data-qa="range-option-custom"
          id={`${htmlId}-range-option-custom`}
          onClick={handleCustomClick}
          role="option"
          type="button"
        >
          {locale.get('customRange')}
        </button>
      )}
    </div>
  );
};

RangeSelect.propTypes = {
  RangeSelectProps: PropTypes.shape({}),
  alwaysShowCalendars: PropTypes.bool.isRequired,
  firstFocusedElement: PropTypes.bool.isRequired,
  htmlId: PropTypes.string,
  onApply: PropTypes.func.isRequired,
  onPresetSelection: PropTypes.func.isRequired,
  rangePresets: PropTypes.shape({}).isRequired,
  selectedRangeName: PropTypes.string,
  setEditing: PropTypes.func.isRequired,
  setHead: PropTypes.func.isRequired,
  setSelectedEndDate: PropTypes.func.isRequired,
  setSelectedRangeName: PropTypes.func.isRequired,
  setSelectedStartDate: PropTypes.func.isRequired,
  showCustomRangeLabel: PropTypes.bool.isRequired,
};

RangeSelect.defaultProps = {
  RangeSelectProps: {},
  htmlId: undefined,
  selectedRangeName: undefined,
};

export default withTheme(RangeSelect);
